<script setup lang="ts">
import type { LengthUnit } from '~/types/elementsUi'
import type { ImageBreakpointType, ResponsiveImageProps } from '~/types/images'
import { getImageAlt, getImageSource } from '~/utils/helpers.ts'

type Format = 'webp' | 'avif' | 'jpeg' | 'jpg' | 'png' | 'gif' | 'svg'

interface BaseVImageProps {
  image: ResponsiveImageProps
  fallback?: ResponsiveImageProps
  breakpointType?: ImageBreakpointType
  format?: Format
  height?: LengthUnit
  width?: LengthUnit
  ariaHidden?: 'true' | 'false'
  loadingType?: 'eager' | 'lazy'
  useImg?: boolean
  sizes?: string
  disablePlaceholder?: boolean
  preload?:
    | boolean
    | {
        fetchPriority: 'auto' | 'high' | 'low'
      }
  modifiers?: { animated?: true }
}

interface NormalImageProps extends BaseVImageProps {
  dark?: never
}

interface DarkImageProps extends BaseVImageProps {
  dark: ResponsiveImageProps | undefined
}

// Can't do withDefaults and discriminated unions together
const props = defineProps<NormalImageProps | DarkImageProps>()

const darkMode = useColorMode()
const { t, te } = useI18n()

const errorLoading = ref(false)

const computedSrc = computed(() => {
  if (errorLoading.value && props.fallback) {
    return getImageSource(props.fallback.src, props.breakpointType)
  } else {
    return getImageSource(props.image.src, props.breakpointType)
  }
})

const computedAlt = computed(() => {
  let alt: string
  if (errorLoading.value && props.fallback) {
    alt = getImageAlt(props.fallback.alt, props.breakpointType)
  } else if (props.dark?.alt && darkMode.value === 'dark') {
    alt = getImageAlt(props.dark.alt, props.breakpointType)
  } else {
    alt = getImageAlt(props.image.alt, props.breakpointType)
  }

  if (te(alt)) {
    return t(alt)
  } else {
    return alt
  }
})

// TODO: Change to avif as soon as pages have been ported to CMS
const computedFormat = computed(() => props.format ?? 'webp')

function onImgError(e: string | Event) {
  if (typeof e === 'string') {
    return
  }

  const el = e.target as HTMLImageElement
  if (
    props.fallback?.src &&
    el?.src !== props.fallback?.src &&
    !el?.dataset.fallbackApplied
  ) {
    errorLoading.value = true

    if (el) {
      el.dataset.fallbackApplied = '1'
    }
  }
}
</script>

<template>
  <UColorModeImage
    v-if="dark?.src"
    :light="computedSrc"
    :dark="getImageSource(dark.src, breakpointType)"
    :alt="computedAlt"
    :loading="loadingType ?? 'lazy'"
    :format="computedFormat"
    :width="width"
    :sizes
    :modifiers
    :preload
    @error="onImgError"
  />
  <NuxtImg
    v-else-if="!useImg"
    :src="computedSrc"
    :alt="computedAlt"
    :height="height"
    :aria-hidden="ariaHidden"
    :loading="loadingType ?? 'lazy'"
    :format="computedFormat"
    :width="width"
    :sizes
    :modifiers
    :preload
    :placeholder="!disablePlaceholder"
    @error="onImgError"
  />
  <img
    v-else
    :src="computedSrc"
    :alt="computedAlt"
    :height="height"
    :width="width"
    :aria-hidden="ariaHidden"
    :loading="loadingType ?? 'lazy'"
    @error="onImgError"
  />
</template>

<style scoped></style>
